/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { useMemo } from "react";
import type { FunctionComponent, JSX } from "react";

import type { MyHubInfoCardProps } from "./MyHubInfoCardProps";
import "./MyHubSeriesInfoCard.scss";

/**
 * MyHubSeriesInfoCard — the recording highlight's text block for an episode
 * (Figma: Unity Design System › InfoCards › Type=Series, node 1961:24821).
 *
 * The series title over "S5:E14 - Episode Title", then
 * "TV-G  Series | 1h 5m | First aired …", then "Expires on …". Anything that
 * is not a movie lands here; without an episode line the title alone takes
 * the main line. The sheet around it — backdrop and action row — is
 * MyHubRecordingHighlight's.
 */
const MyHubSeriesInfoCard: FunctionComponent<MyHubInfoCardProps> = (
  props: MyHubInfoCardProps
): JSX.Element => {
  const { item } = props;
  const ratingBadge: string = item.tile.ratingBadge || "";
  const hasEpisodeLine: boolean = item.episodeLabel !== "";

  // Whichever of type / duration / first aired the item carries, in that
  // reading order.
  const infoFragments: string[] = useMemo(
    (): string[] =>
      [item.typeLabel, item.durationLabel, item.firstAiredLabel].filter(
        (fragment: string): boolean => fragment !== ""
      ),
    [item.typeLabel, item.durationLabel, item.firstAiredLabel]
  );

  return (
    <div
      className="my-hub-series-info-card"
      data-testid="my-hub-series-info-card"
    >
      <div className="my-hub-series-info-card__heading">
        {hasEpisodeLine && (
          <p className="my-hub-series-info-card__series-title">{item.title}</p>
        )}
        <h2 className="my-hub-series-info-card__episode-title">
          {hasEpisodeLine ? item.episodeLabel : item.title}
        </h2>
      </div>

      {(ratingBadge !== "" || infoFragments.length > 0) && (
        <div className="my-hub-series-info-card__info-row">
          {ratingBadge !== "" && (
            <span className="my-hub-recording-highlight__rating-badge">
              {ratingBadge}
            </span>
          )}
          {infoFragments.map(
            (fragment: string, index: number): JSX.Element[] => {
              const key: string = fragment + "-" + String(index);
              const text: JSX.Element = (
                <span key={key} className="my-hub-series-info-card__info-item">
                  {fragment}
                </span>
              );
              return index === 0
                ? [text]
                : [
                    <span
                      key={key + "-divider"}
                      className="my-hub-series-info-card__divider"
                      aria-hidden="true"
                    />,
                    text
                  ];
            }
          )}
        </div>
      )}

      {item.expiresOnLabel !== "" && (
        <p className="my-hub-recording-highlight__expires">
          {item.expiresOnLabel}
        </p>
      )}
    </div>
  );
};

export default MyHubSeriesInfoCard;
